import { ApiError } from "@jobenvoy/core";
import { CONSENT_VERSIONS } from "@/legal/details";
import type { ConsentRecord, Consents, ConsentVersions } from "@/lib/api/auth";
import { BUILD } from "@/lib/build";
import { describeError } from "@/lib/api/client";

/**
 * What a person agreed to, as data: the terms and the age check at sign
 * up and after, and the analytics choice, for a person signed in (kept
 * by the auth service) and for a visitor (kept in this browser). Pure,
 * but for the browser storage, which is read and written behind
 * try/catch so a blocked or full store only means asking again.
 */

/** The sign-up check box and the prompt's: one sentence covers both records. */
export const TERMS_CHECK = "I am 18 or older and I agree to the Terms and the Privacy policy";

/** The prompt's check box when only the age is missing. */
export const AGE_CHECK = "I am 18 or older";

/**
 * The analytics consent text in force, at `CONSENT_VERSIONS.analytics`.
 * The banner and the account switch show it; a change here is a new
 * version, and the auth service's with it.
 */
export const ANALYTICS_TEXT =
    "May we use analytics? With your yes, PostHog records how you use Job Envoy: page views, clicks, errors and session recordings with every input and all text masked. It helps us find what is broken or slow. Nothing is recorded until you say yes, and you can change your mind at any time.";

/**
 * The banner's words where analytics is on until the person turns it off,
 * shown when they open the question from the footer. A no is a refusal;
 * a yes is a grant, which adds session recordings.
 */
export const ANALYTICS_DEFAULT_TEXT =
    "Job Envoy uses analytics. PostHog counts page views, clicks and errors, which helps us find what is broken or slow. Session recordings, with every input and all text masked, start only if you allow them. You can turn analytics off here or on your account page at any time, and Global Privacy Control turns it off too.";

/**
 * Where analytics waits for a yes, by the country code Cloudflare gives:
 * the European Union with its outermost regions and Åland, the rest of
 * the European Economic Area, the United Kingdom and Switzerland.
 */
const ASKS_FIRST: ReadonlySet<string> = new Set(
    [
        // The European Union.
        "AT BE BG CY CZ DE DK EE ES FI FR GR HR HU IE IT LT LU LV MT NL PL PT RO SE SI SK",
        // Its outermost regions and Åland, which Cloudflare names apart.
        "AX GF GP MF MQ RE YT",
        // The rest of the European Economic Area, the UK and Switzerland.
        "IS LI NO GB CH",
    ].flatMap((codes) => codes.split(" ")),
);

/** Codes Cloudflare gives for no country: unknown, and Tor. */
const NO_COUNTRY: ReadonlySet<string> = new Set(["XX", "T1"]);

/**
 * Whether analytics waits for a yes where the visitor is. A place that
 * cannot be read is treated as one that asks first.
 */
export function asksFirst(country: string | null | undefined): boolean {
    const code = country?.trim().toUpperCase() ?? "";
    if (!/^[A-Z][A-Z0-9]$/.test(code) || NO_COUNTRY.has(code)) return true;
    return ASKS_FIRST.has(code);
}

/**
 * Whether a visitor's analytics runs: their kept choice, else on by
 * default only where nothing asks first. Global Privacy Control is a no.
 */
export function visitorAnalyticsOn(
    choice: VisitorChoice,
    askFirst: boolean,
    privacy: boolean,
): boolean {
    if (privacy || choice === "refused") return false;
    return choice === "granted" || !askFirst;
}

/**
 * Whether a signed-in person's analytics runs. A grant always counts and
 * a withdrawal always holds. With no answer under the words in force,
 * analytics waits for one where the person is asked first; elsewhere it
 * follows their last answer, or is on when they never gave one, unless
 * their browser sends Global Privacy Control.
 */
export function recordAnalyticsOn(
    consents: Consents,
    askFirst: boolean,
    privacy: boolean,
): boolean {
    const { status, latest } = consents.analytics;
    if (status === "GRANTED") return true;
    if (status === "WITHDRAWN" || askFirst || privacy) return false;
    return status === "UNASKED" || latest?.granted === true;
}

/** Where a signed-in person must accept before going on. */
export type Acceptance = "none" | "terms" | "age";

/** What the prompt must ask for: the terms (with the age), the age alone, or nothing. */
export function acceptanceNeeded(consents: Consents): Acceptance {
    if (consents.terms.status !== "CURRENT") return "terms";
    if (consents.age.status !== "CONFIRMED") return "age";
    return "none";
}

/**
 * Whether this build shows the texts the service has in force. A build
 * older than the service would ask the person to accept words they never
 * read, so it asks them to reload instead.
 */
export function termsInForce(required: ConsentVersions): boolean {
    return (
        required.terms_version === CONSENT_VERSIONS.terms &&
        required.privacy_version === CONSENT_VERSIONS.privacy
    );
}

export function analyticsInForce(required: ConsentVersions): boolean {
    return required.analytics_version === CONSENT_VERSIONS.analytics;
}

/** Where every record the webapp makes comes from: the webapp, and this build of it. */
const FROM_HERE = { source: "WEBAPP", client_version: BUILD } as const;

/** The record the terms check box makes: the terms, the privacy policy and the age, at once. */
export function termsRecord(action: "SIGNUP_CHECKBOX" | "REACCEPT_PROMPT"): ConsentRecord {
    return {
        kind: "TERMS",
        terms_version: CONSENT_VERSIONS.terms,
        privacy_version: CONSENT_VERSIONS.privacy,
        action,
        age_confirmed: true,
        ...FROM_HERE,
    };
}

export function ageRecord(): ConsentRecord {
    return { kind: "AGE", action: "REACCEPT_PROMPT", ...FROM_HERE };
}

export function analyticsRecord(
    granted: boolean,
    action: "CONSENT_BANNER" | "SETTINGS_TOGGLE",
): ConsentRecord {
    return {
        kind: "ANALYTICS",
        granted,
        version: CONSENT_VERSIONS.analytics,
        action,
        ...FROM_HERE,
    };
}

/** Whether the person needs asking about analytics: never asked, or asked under older words. */
export function analyticsUnasked(consents: Consents): boolean {
    return consents.analytics.status === "UNASKED" || consents.analytics.status === "BEHIND";
}

/** A refused record, said plainly. */
export function consentError(error: unknown): string {
    if (error instanceof ApiError) {
        if (error.status === 412)
            return "The terms changed while this page was open. Reload to read the new ones.";
        if (error.status === 429)
            return "Too many changes in the last hour. Try again in a little while.";
        if (error.status === 400) return "That was not accepted. Reload the page and try again.";
    }
    return describeError(error);
}

/** Global Privacy Control: on, a visitor is never asked and analytics never starts for them. */
export function privacySignal(nav: Navigator | undefined = globalThis.navigator): boolean {
    try {
        return (
            (nav as { globalPrivacyControl?: unknown } | undefined)?.globalPrivacyControl === true
        );
    } catch {
        return false;
    }
}

/* The sign-up check box, kept across the sign-in's round trip. */

const SIGNUP_KEY = "jobenvoy-signup-terms-v1";

/** How long a ticked box waits for the sign-in to come back: longer is another visit. */
const SIGNUP_TTL_MS = 30 * 60_000;

export interface SignupAcceptance {
    terms_version: string;
    privacy_version: string;
    /** When the box was ticked, in ms. */
    at: number;
}

/** Keeps that the box was ticked, with the versions shown. Says whether it was kept. */
export function keepSignupAcceptance(store: Storage | undefined, now = Date.now()): boolean {
    if (!store) return false;
    const kept: SignupAcceptance = {
        terms_version: CONSENT_VERSIONS.terms,
        privacy_version: CONSENT_VERSIONS.privacy,
        at: now,
    };
    try {
        store.setItem(SIGNUP_KEY, JSON.stringify(kept));
        return true;
    } catch {
        return false;
    }
}

/**
 * The ticked box, if this page kept one. Nothing for one that is stale,
 * from the future, or not what this page wrote. Reading leaves it in
 * place: `forgetSignupAcceptance` takes it out once it is used.
 */
export function readSignupAcceptance(
    store: Storage | undefined,
    now = Date.now(),
): SignupAcceptance | null {
    if (!store) return null;
    let raw: string | null;
    try {
        raw = store.getItem(SIGNUP_KEY);
    } catch {
        return null;
    }
    if (!raw) return null;
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    if (!parsed || typeof parsed !== "object") return null;
    const { terms_version, privacy_version, at } = parsed as Record<string, unknown>;
    if (typeof terms_version !== "string" || typeof privacy_version !== "string") return null;
    if (typeof at !== "number" || !Number.isFinite(at)) return null;
    if (at > now || now - at > SIGNUP_TTL_MS) return null;
    return { terms_version, privacy_version, at };
}

/** How far ahead of this clock the sign-in's own may run and still be believed. */
const CLOCK_SKEW_MS = 5 * 60_000;

/**
 * Whether the account was made within the same half hour a ticked box
 * waits for the sign-in. An account made at its first Google sign-in
 * never sees the sign-up sheet, so its first agreement comes from the
 * prompt; an account made before consent records were kept also arrives
 * with none, and is not new. A time that does not read is not new.
 */
export function accountJustMade(createdAt: string | null | undefined, now = Date.now()): boolean {
    if (!createdAt) return false;
    const at = Date.parse(createdAt);
    if (!Number.isFinite(at)) return false;
    return at - now <= CLOCK_SKEW_MS && now - at <= SIGNUP_TTL_MS;
}

/** Takes the ticked box out, so it is recorded once at most. */
export function forgetSignupAcceptance(store: Storage | undefined): void {
    try {
        store?.removeItem(SIGNUP_KEY);
    } catch {
        // A store that will not let go: the box ages out on its own.
    }
}

/** Whether a ticked box was for the versions in force, so it can be recorded as given. */
export function signupMatches(kept: SignupAcceptance, required: ConsentVersions): boolean {
    return (
        termsInForce(required) &&
        kept.terms_version === required.terms_version &&
        kept.privacy_version === required.privacy_version
    );
}

/* A visitor's analytics choice, in this browser. */

const VISITOR_KEY = "jobenvoy-analytics-choice-v1";

export type VisitorChoice = "granted" | "refused" | "unasked";

/** The visitor's choice, under the analytics text in force. Asked again after a change. */
export function readVisitorChoice(store: Storage | undefined): VisitorChoice {
    if (!store) return "unasked";
    let raw: string | null;
    try {
        raw = store.getItem(VISITOR_KEY);
    } catch {
        return "unasked";
    }
    if (!raw) return "unasked";
    try {
        const parsed = JSON.parse(raw) as { granted?: unknown; version?: unknown };
        if (parsed?.version !== CONSENT_VERSIONS.analytics) return "unasked";
        if (parsed.granted === true) return "granted";
        if (parsed.granted === false) return "refused";
    } catch {
        // Not what this page wrote: ask again.
    }
    return "unasked";
}

/** Keeps the visitor's choice. False when the browser would not keep it. */
export function keepVisitorChoice(
    store: Storage | undefined,
    granted: boolean,
    now = Date.now(),
): boolean {
    if (!store) return false;
    try {
        store.setItem(
            VISITOR_KEY,
            JSON.stringify({ granted, version: CONSENT_VERSIONS.analytics, at: now }),
        );
        return true;
    } catch {
        return false;
    }
}

/** A storage area, or nothing when the browser will not hand it over. */
export function storageOf(kind: "localStorage" | "sessionStorage"): Storage | undefined {
    try {
        return window[kind];
    } catch {
        return undefined;
    }
}
